import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import axios from 'axios';
import { toast } from 'sonner';
import DOMPurify from 'dompurify';
import { Button } from '../../components/ui/button';
import {
    ArrowLeft, Sparkles, Loader2, Save, Play, Pause, X, CheckCircle,
    FileText, Target, MessageSquare, Settings, Users, Briefcase,
    ChevronDown, GripVertical, ExternalLink, Link, Copy, Share2,
    Video, Send, RefreshCw, Award, Clock as ClockIcon, AlertCircle,
} from 'lucide-react';
import { DndContext, DragOverlay, useDroppable, useDraggable, PointerSensor, useSensor, useSensors, closestCenter } from '@dnd-kit/core';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUS_COLORS = {
    draft: 'bg-stone-100 text-stone-600',
    for_review: 'bg-blue-100 text-blue-700',
    active: 'bg-emerald-100 text-emerald-700',
    on_hold: 'bg-amber-100 text-amber-700',
    completed: 'bg-green-100 text-green-700',
    cancelled: 'bg-red-100 text-red-600',
};

const TABS = [
    { key: 'pipeline', label: 'Pipeline', icon: Users },
    { key: 'description', label: 'Description', icon: FileText },
    { key: 'matching', label: 'Matching Criteria', icon: Target },
    { key: 'interview', label: 'Interview Questions', icon: MessageSquare },
    { key: 'settings', label: 'Settings', icon: Settings },
];

export default function ATSJobDetail() {
    const { jobId } = useParams();
    const { getAuthHeader } = useAuth();
    const navigate = useNavigate();
    const [job, setJob] = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState('pipeline');
    const [generating, setGenerating] = useState({});
    const [applications, setApplications] = useState([]);
    const [editingTitle, setEditingTitle] = useState(false);
    const [titleDraft, setTitleDraft] = useState('');

    const loadJob = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/ats/jobs/${jobId}`, { headers: getAuthHeader() });
            setJob(res.data);
            setTitleDraft(res.data.title);
        } catch { toast.error('Failed to load job'); navigate('/recruitment'); }
        finally { setLoading(false); }
    }, [jobId, getAuthHeader, navigate]);

    const loadApplications = useCallback(async () => {
        try {
            const res = await axios.get(`${API}/ats/applications/job/${jobId}`, { headers: getAuthHeader() });
            setApplications(res.data?.data || res.data || []);
        } catch { /* Applications may not exist yet */ }
    }, [jobId, getAuthHeader]);

    useEffect(() => { loadJob(); loadApplications(); }, [loadJob, loadApplications]);

    const handleGenerate = async (type) => {
        setGenerating(g => ({ ...g, [type]: true }));
        try {
            const res = await axios.post(`${API}/ats/jobs/${jobId}/generate-${type}`, {}, { headers: getAuthHeader() });
            toast.success(`${type.replace(/-/g, ' ')} generated`);
            await loadJob();
        } catch (err) { toast.error(err.response?.data?.detail || `Failed to generate ${type}`); }
        finally { setGenerating(g => ({ ...g, [type]: false })); }
    };

    const handleStatusChange = async (newStatus) => {
        try {
            await axios.patch(`${API}/ats/jobs/${jobId}/status`, { status: newStatus }, { headers: getAuthHeader() });
            toast.success(`Status updated to ${newStatus}`);
            await loadJob();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update status'); }
    };

    const handleSaveTitle = async () => {
        if (!titleDraft.trim()) return;
        try {
            await axios.put(`${API}/ats/jobs/${jobId}`, { title: titleDraft }, { headers: getAuthHeader() });
            setEditingTitle(false);
            await loadJob();
        } catch { toast.error('Failed to save title'); }
    };

    if (loading) return (
        <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 animate-spin text-stone-400" />
        </div>
    );

    if (!job) return null;

    // Group applications by stage for pipeline view
    const stageGroups = {};
    applications.forEach(app => {
        const key = app.stage || 'applied';
        if (!stageGroups[key]) stageGroups[key] = [];
        stageGroups[key].push(app);
    });

    return (
        <div className="space-y-6">
            {/* Header */}
            <div className="flex items-start gap-4">
                <button onClick={() => navigate('/recruitment')} className="mt-1 p-1 rounded hover:bg-stone-100">
                    <ArrowLeft className="w-5 h-5 text-stone-400" />
                </button>
                <div className="flex-1">
                    <div className="flex items-center gap-3">
                        {editingTitle ? (
                            <div className="flex items-center gap-2">
                                <input value={titleDraft} onChange={e => setTitleDraft(e.target.value)}
                                    className="text-xl font-bold text-stone-900 border-b-2 border-stone-900 focus:outline-none bg-transparent"
                                    style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
                                    onKeyDown={e => e.key === 'Enter' && handleSaveTitle()} autoFocus />
                                <Button size="sm" variant="ghost" onClick={handleSaveTitle}><Save className="w-4 h-4" /></Button>
                                <Button size="sm" variant="ghost" onClick={() => setEditingTitle(false)}><X className="w-4 h-4" /></Button>
                            </div>
                        ) : (
                            <h1 className="text-xl font-bold text-stone-900 cursor-pointer hover:text-stone-700"
                                style={{ fontFamily: 'Manrope, system-ui, sans-serif' }}
                                onClick={() => setEditingTitle(true)}>
                                {job.title}
                            </h1>
                        )}
                        <span className={`inline-flex px-2 py-0.5 rounded-full text-xs font-medium ${STATUS_COLORS[job.status] || 'bg-stone-100'}`}>
                            {job.status?.replace(/_/g, ' ')}
                        </span>
                    </div>
                    <div className="flex items-center gap-4 mt-1 text-sm text-stone-500">
                        {job.account_name && <span className="flex items-center gap-1"><Briefcase className="w-3.5 h-3.5" />{job.account_name}</span>}
                        {job.department && <span>{job.department}</span>}
                        <span>{job.employment_type?.replace(/_/g, ' ')}</span>
                        <span>{job.filled_slots || 0}/{job.slots} slots filled</span>
                    </div>
                </div>
                <div className="flex items-center gap-2">
                    {job.status === 'draft' && (
                        <Button onClick={() => handleStatusChange('active')} className="bg-emerald-600 text-white hover:bg-emerald-700">
                            <Play className="w-4 h-4 mr-1" /> Publish
                        </Button>
                    )}
                    {job.status === 'active' && (
                        <Button onClick={() => handleStatusChange('on_hold')} variant="outline">
                            <Pause className="w-4 h-4 mr-1" /> Hold
                        </Button>
                    )}
                    {job.status === 'on_hold' && (
                        <Button onClick={() => handleStatusChange('active')} variant="outline">
                            <Play className="w-4 h-4 mr-1" /> Resume
                        </Button>
                    )}
                    {(job.status === 'active' || job.status === 'on_hold') && (
                        <Button onClick={() => handleStatusChange('completed')} variant="outline">
                            <CheckCircle className="w-4 h-4 mr-1" /> Complete
                        </Button>
                    )}
                </div>
            </div>

            {/* Tabs */}
            <div className="border-b border-stone-200">
                <div className="flex gap-1">
                    {TABS.map(t => (
                        <button key={t.key} onClick={() => setTab(t.key)}
                            className={`flex items-center gap-1.5 px-4 py-2.5 text-sm font-medium border-b-2 transition-colors ${
                                tab === t.key
                                    ? 'border-stone-900 text-stone-900'
                                    : 'border-transparent text-stone-500 hover:text-stone-700'
                            }`}>
                            <t.icon className="w-4 h-4" /> {t.label}
                            {t.key === 'pipeline' && applications.length > 0 && (
                                <span className="ml-1 px-1.5 py-0.5 rounded-full bg-stone-100 text-xs">{applications.length}</span>
                            )}
                        </button>
                    ))}
                </div>
            </div>

            {/* Tab Content */}
            {tab === 'pipeline' && (
                <PipelineView stageGroups={stageGroups} applications={applications} getAuthHeader={getAuthHeader} onRefresh={() => { loadJob(); loadApplications(); }} job={job} />
            )}

            {tab === 'description' && (
                <ContentSection
                    title="Job Description"
                    content={job.description}
                    onGenerate={() => handleGenerate('description')}
                    generating={generating['description']}
                    generateLabel="Generate with AI"
                />
            )}

            {tab === 'matching' && (
                <ContentSection
                    title="Matching Criteria"
                    content={job.matching_criteria_md}
                    onGenerate={() => handleGenerate('matching-criteria')}
                    generating={generating['matching-criteria']}
                    generateLabel="Generate Criteria"
                />
            )}

            {tab === 'interview' && (
                <div className="space-y-6">
                    {/* InterviewRoom.ai Integration */}
                    <InterviewRoomSection job={job} applications={applications} getAuthHeader={getAuthHeader} onRefresh={loadJob} />

                    {/* Interview Questions */}
                    <div className="space-y-4">
                        <div className="flex items-center justify-between">
                            <h3 className="font-semibold text-stone-900">Interview Questions</h3>
                            <Button onClick={() => handleGenerate('interview-questions')} disabled={generating['interview-questions']}
                                variant="outline" size="sm">
                                {generating['interview-questions']
                                    ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Generating...</>
                                    : <><Sparkles className="w-4 h-4 mr-1" /> Generate Questions</>}
                            </Button>
                        </div>
                        {(job.interview_questions || []).length === 0 ? (
                            <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
                                <MessageSquare className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                                <p className="text-sm text-stone-500">No interview questions yet</p>
                                <p className="text-xs text-stone-400 mt-1">Click "Generate Questions" to create AI-powered Q&A with rubrics</p>
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {job.interview_questions.map((q, i) => (
                                    <div key={i} className="bg-white rounded-xl border border-stone-200 p-4">
                                        <div className="flex items-start gap-3">
                                            <span className="text-xs font-bold text-stone-400 mt-0.5">{i + 1}</span>
                                            <div className="flex-1">
                                                <p className="font-medium text-stone-900 text-sm">{q.question}</p>
                                                {q.category && <span className="text-xs text-stone-400 mt-1 inline-block">{q.category}</span>}
                                                {q.rubric && (
                                                    <div className="mt-3 grid grid-cols-3 gap-2">
                                                        {[['poor', 'red'], ['good', 'amber'], ['excellent', 'emerald']].map(([level, color]) => (
                                                            <div key={level} className={`p-2 rounded-lg bg-${color}-50 border border-${color}-100`}>
                                                                <p className={`text-xs font-semibold text-${color}-700 uppercase mb-1`}>{level}</p>
                                                                <p className="text-xs text-stone-600">{q.rubric[level]}</p>
                                                            </div>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            )}

            {tab === 'settings' && (
                <div className="space-y-6">
                    <div className="bg-white rounded-xl border border-stone-200 p-6">
                        <h3 className="font-semibold text-stone-900 mb-4">Job Settings</h3>
                        <div className="grid grid-cols-2 gap-4 text-sm">
                            <div><span className="text-stone-500">Employment Type:</span> <span className="font-medium ml-2">{job.employment_type?.replace(/_/g, ' ')}</span></div>
                            <div><span className="text-stone-500">Work Location:</span> <span className="font-medium ml-2">{job.work_location}</span></div>
                            <div><span className="text-stone-500">Shift:</span> <span className="font-medium ml-2">{job.shift || 'Day'}</span></div>
                            <div><span className="text-stone-500">Experience Level:</span> <span className="font-medium ml-2">{job.experience_level || 'Mid'}</span></div>
                            <div><span className="text-stone-500">Owner:</span> <span className="font-medium ml-2">{job.owner_name || '—'}</span></div>
                            <div><span className="text-stone-500">Created:</span> <span className="font-medium ml-2">{new Date(job.created_at).toLocaleDateString()}</span></div>
                        </div>
                    </div>
                    <ClientPortalSection job={job} getAuthHeader={getAuthHeader} />
                </div>
            )}
        </div>
    );
}

function ContentSection({ title, content, onGenerate, generating, generateLabel }) {
    return (
        <div className="space-y-4">
            <div className="flex items-center justify-between">
                <h3 className="font-semibold text-stone-900">{title}</h3>
                <Button onClick={onGenerate} disabled={generating} variant="outline" size="sm">
                    {generating
                        ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Generating...</>
                        : <><Sparkles className="w-4 h-4 mr-1" /> {generateLabel}</>}
                </Button>
            </div>
            {content ? (
                <div className="bg-white rounded-xl border border-stone-200 p-6 prose prose-stone prose-sm max-w-none"
                    dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(content.replace(/\n/g, '<br/>')) }} />
            ) : (
                <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
                    <FileText className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">No content yet</p>
                    <p className="text-xs text-stone-400 mt-1">Click "{generateLabel}" to create AI-powered content</p>
                </div>
            )}
        </div>
    );
}

function ClientPortalSection({ job, getAuthHeader }) {
    const [tokens, setTokens] = useState([]);
    const [loading, setLoading] = useState(false);
    const [creating, setCreating] = useState(false);
    const [copied, setCopied] = useState(null);

    const loadTokens = useCallback(async () => {
        setLoading(true);
        try {
            const res = await axios.get(`${API}/ats/client-portal/tokens?account_id=${job.account_id || ''}`, { headers: getAuthHeader() });
            const all = res.data?.data || [];
            // Filter to tokens that include this job
            setTokens(all.filter(t => !t.job_ids?.length || t.job_ids.includes(job.job_id)));
        } catch { /* may not have tokens */ }
        finally { setLoading(false); }
    }, [job, getAuthHeader]);

    useEffect(() => { if (job.account_id) loadTokens(); }, [loadTokens, job.account_id]);

    const handleCreate = async () => {
        if (!job.account_id) return toast.error('This job has no linked client account. Set an account first.');
        setCreating(true);
        try {
            const res = await axios.post(`${API}/ats/client-portal/tokens`, {
                account_id: job.account_id,
                contact_id: job.client_contact_id || '',
                job_ids: [job.job_id],
                send_email: false,
                expires_days: 30,
            }, { headers: getAuthHeader() });
            toast.success('Portal link created');
            loadTokens();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create portal link'); }
        finally { setCreating(false); }
    };

    const handleCopy = (token) => {
        const url = `${window.location.origin}/client-portal?token=${token}`;
        navigator.clipboard.writeText(url);
        setCopied(token);
        toast.success('Link copied');
        setTimeout(() => setCopied(null), 2000);
    };

    const handleRevoke = async (tokenId) => {
        try {
            await axios.delete(`${API}/ats/client-portal/tokens/${tokenId}`, { headers: getAuthHeader() });
            toast.success('Token revoked');
            loadTokens();
        } catch { toast.error('Failed to revoke'); }
    };

    return (
        <div className="bg-white rounded-xl border border-stone-200 p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Share2 className="w-4 h-4 text-stone-500" />
                    <h3 className="font-semibold text-stone-900">Client Portal</h3>
                </div>
                <Button onClick={handleCreate} disabled={creating || !job.account_id} size="sm" className="bg-stone-900 text-white hover:bg-stone-800">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Link className="w-4 h-4 mr-1" />}
                    Generate Portal Link
                </Button>
            </div>

            {!job.account_id ? (
                <p className="text-sm text-stone-400">Link this job to a client account to enable the client portal.</p>
            ) : tokens.length === 0 ? (
                <p className="text-sm text-stone-400">No active portal links. Generate one to share candidates with {job.account_name || 'the client'}.</p>
            ) : (
                <div className="space-y-2">
                    {tokens.map(t => (
                        <div key={t.token_id} className="flex items-center gap-3 px-3 py-2.5 bg-stone-50 rounded-lg border border-stone-200">
                            <Link className="w-4 h-4 text-stone-400 flex-shrink-0" />
                            <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-stone-700 truncate">
                                    {t.contact_name || t.account_name || 'Client Portal'}
                                </p>
                                <p className="text-xs text-stone-400">
                                    Expires {new Date(t.expires_at).toLocaleDateString()} · {t.total_views || 0} views
                                </p>
                            </div>
                            <div className="flex items-center gap-1">
                                <button onClick={() => handleCopy(t.token)}
                                    className={`p-1.5 rounded-lg transition-colors ${copied === t.token ? 'bg-emerald-100 text-emerald-600' : 'hover:bg-stone-100 text-stone-400'}`}
                                    title="Copy link">
                                    <Copy className="w-3.5 h-3.5" />
                                </button>
                                <button onClick={() => handleRevoke(t.token_id)}
                                    className="p-1.5 rounded-lg hover:bg-red-50 text-stone-400 hover:text-red-500 transition-colors"
                                    title="Revoke">
                                    <X className="w-3.5 h-3.5" />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
}

function InterviewRoomSection({ job, applications, getAuthHeader, onRefresh }) {
    const [irStatus, setIrStatus] = useState(null);
    const [configStatus, setConfigStatus] = useState(null);
    const [creating, setCreating] = useState(false);
    const [sending, setSending] = useState({});
    const [bulkSending, setBulkSending] = useState(false);
    const [loading, setLoading] = useState(true);

    const loadIR = useCallback(async () => {
        setLoading(true);
        try {
            const [roomRes, configRes] = await Promise.allSettled([
                axios.get(`${API}/ats/interviews/rooms/${job.job_id}`, { headers: getAuthHeader() }),
                axios.get(`${API}/ats/interviews/config-status`, { headers: getAuthHeader() }),
            ]);
            if (roomRes.status === 'fulfilled') setIrStatus(roomRes.value.data);
            if (configRes.status === 'fulfilled') setConfigStatus(configRes.value.data);
        } catch { /* no IR config yet */ }
        finally { setLoading(false); }
    }, [job.job_id, getAuthHeader]);

    useEffect(() => { loadIR(); }, [loadIR]);

    const handleCreateRoom = async () => {
        setCreating(true);
        try {
            await axios.post(`${API}/ats/interviews/rooms`, { job_id: job.job_id }, { headers: getAuthHeader() });
            toast.success('Interview room created');
            loadIR();
            onRefresh?.();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to create room'); }
        finally { setCreating(false); }
    };

    const handleSendInvite = async (applicationId) => {
        setSending(s => ({ ...s, [applicationId]: true }));
        try {
            const res = await axios.post(`${API}/ats/interviews/invite`, {
                application_id: applicationId,
                interview_type: 'internal',
            }, { headers: getAuthHeader() });
            toast.success(`Invite sent to ${res.data?.applicant_name || 'applicant'}`);
            loadIR();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send invite'); }
        finally { setSending(s => ({ ...s, [applicationId]: false })); }
    };

    const handleBulkInvite = async () => {
        const eligible = applications.filter(a =>
            a.status === 'active' &&
            ['initial_interview', 'client_interview'].includes(a.stage) &&
            !(a.interview_results || []).some(r => r.status !== 'completed')
        );
        if (!eligible.length) return toast.error('No eligible applicants in interview stages');

        setBulkSending(true);
        try {
            const res = await axios.post(`${API}/ats/interviews/invite/bulk`, {
                application_ids: eligible.map(a => a.application_id),
                interview_type: 'internal',
            }, { headers: getAuthHeader() });
            toast.success(`${res.data?.sent || 0} invites sent, ${res.data?.failed || 0} failed`);
            loadIR();
        } catch (err) { toast.error(err.response?.data?.detail || 'Failed to send bulk invites'); }
        finally { setBulkSending(false); }
    };

    if (loading) {
        return (
            <div className="bg-white rounded-xl border border-stone-200 p-6">
                <div className="flex items-center gap-2 text-stone-400">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    <span className="text-sm">Loading InterviewRoom status...</span>
                </div>
            </div>
        );
    }

    const isConfigured = configStatus?.configured;
    const hasRoom = irStatus?.configured;
    const invites = irStatus?.invites || [];
    const hasQuestions = (job.interview_questions || []).length > 0;

    // Determine which applicants are in interview stages and can be invited
    const interviewStageApps = applications.filter(a =>
        a.status === 'active' && ['initial_interview', 'client_interview'].includes(a.stage)
    );

    const invitedIds = new Set(invites.map(inv => inv.application_id));

    return (
        <div className="bg-white rounded-xl border border-stone-200 p-6">
            <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                    <Video className="w-5 h-5 text-violet-500" />
                    <h3 className="font-semibold text-stone-900">InterviewRoom.ai</h3>
                    {hasRoom && (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">
                            <CheckCircle className="w-3 h-3" /> Room Active
                        </span>
                    )}
                </div>
                <div className="flex items-center gap-2">
                    {hasRoom && (
                        <>
                            <Button onClick={handleBulkInvite} disabled={bulkSending || !interviewStageApps.length}
                                variant="outline" size="sm">
                                {bulkSending
                                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Sending...</>
                                    : <><Send className="w-3.5 h-3.5 mr-1" /> Invite All in Interview ({interviewStageApps.length})</>}
                            </Button>
                            <Button onClick={loadIR} variant="ghost" size="sm" title="Refresh">
                                <RefreshCw className="w-3.5 h-3.5" />
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {!isConfigured ? (
                <div className="flex items-center gap-3 p-4 rounded-lg bg-amber-50 border border-amber-100">
                    <AlertCircle className="w-5 h-5 text-amber-500 flex-shrink-0" />
                    <div>
                        <p className="text-sm font-medium text-amber-800">InterviewRoom.ai not configured</p>
                        <p className="text-xs text-amber-600 mt-0.5">Add your API key in Recruitment Settings to enable video interviews.</p>
                    </div>
                </div>
            ) : !hasRoom ? (
                <div className="space-y-3">
                    <p className="text-sm text-stone-500">
                        Create an interview room to send video interviews to applicants. Questions will be synced from the list below.
                    </p>
                    <Button onClick={handleCreateRoom} disabled={creating || !hasQuestions}
                        className="bg-violet-600 text-white hover:bg-violet-700">
                        {creating
                            ? <><Loader2 className="w-4 h-4 animate-spin mr-1" /> Creating Room...</>
                            : <><Video className="w-4 h-4 mr-1" /> Create Interview Room</>}
                    </Button>
                    {!hasQuestions && (
                        <p className="text-xs text-stone-400">Generate interview questions first before creating a room.</p>
                    )}
                </div>
            ) : (
                <div className="space-y-4">
                    {/* Room Stats */}
                    <div className="grid grid-cols-4 gap-3">
                        {[
                            { label: 'Questions', value: irStatus?.questions_count || 0, icon: MessageSquare, color: 'text-violet-600' },
                            { label: 'Invited', value: invites.length, icon: Send, color: 'text-blue-600' },
                            { label: 'Completed', value: invites.filter(i => i.status === 'completed').length, icon: CheckCircle, color: 'text-emerald-600' },
                            { label: 'Avg Score', value: (() => {
                                const scored = invites.filter(i => i.score != null);
                                return scored.length ? `${Math.round(scored.reduce((s, i) => s + i.score, 0) / scored.length)}%` : '—';
                            })(), icon: Award, color: 'text-amber-600' },
                        ].map(stat => (
                            <div key={stat.label} className="bg-stone-50 rounded-lg p-3 text-center">
                                <stat.icon className={`w-4 h-4 ${stat.color} mx-auto mb-1`} />
                                <p className="text-lg font-bold text-stone-800">{stat.value}</p>
                                <p className="text-xs text-stone-400">{stat.label}</p>
                            </div>
                        ))}
                    </div>

                    {/* Invite List for Interview-Stage Applicants */}
                    {interviewStageApps.length > 0 && (
                        <div>
                            <h4 className="text-sm font-medium text-stone-600 mb-2">Applicants in Interview Stages</h4>
                            <div className="space-y-1.5">
                                {interviewStageApps.map(app => {
                                    const invite = invites.find(i => i.application_id === app.application_id);
                                    const isInvited = !!invite;
                                    const isCompleted = invite?.status === 'completed';
                                    return (
                                        <div key={app.application_id} className="flex items-center gap-3 px-3 py-2 bg-stone-50 rounded-lg">
                                            <div className="flex-1 min-w-0">
                                                <p className="text-sm font-medium text-stone-800 truncate">{app.applicant_name}</p>
                                                <p className="text-xs text-stone-400">{app.stage_label || app.stage}</p>
                                            </div>
                                            {isCompleted ? (
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs font-semibold text-emerald-600">{invite.score != null ? `${Math.round(invite.score)}%` : '—'}</span>
                                                    {invite.playback_url && (
                                                        <a href={invite.playback_url} target="_blank" rel="noopener noreferrer"
                                                            className="p-1 rounded hover:bg-stone-200 text-stone-500">
                                                            <Play className="w-3.5 h-3.5" />
                                                        </a>
                                                    )}
                                                    <span className="px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-medium">Done</span>
                                                </div>
                                            ) : isInvited ? (
                                                <span className="px-2 py-0.5 rounded-full bg-blue-50 text-blue-600 text-xs font-medium flex items-center gap-1">
                                                    <ClockIcon className="w-3 h-3" /> Pending
                                                </span>
                                            ) : (
                                                <Button onClick={() => handleSendInvite(app.application_id)}
                                                    disabled={sending[app.application_id]} size="sm" variant="outline"
                                                    className="text-xs h-7">
                                                    {sending[app.application_id]
                                                        ? <Loader2 className="w-3 h-3 animate-spin" />
                                                        : <><Send className="w-3 h-3 mr-1" /> Invite</>}
                                                </Button>
                                            )}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    )}

                    {/* Completed Interviews */}
                    {invites.filter(i => i.status === 'completed').length > 0 && (
                        <div>
                            <h4 className="text-sm font-medium text-stone-600 mb-2">Completed Interviews</h4>
                            <div className="space-y-1.5">
                                {invites.filter(i => i.status === 'completed').map(inv => (
                                    <div key={inv.invite_id} className="flex items-center gap-3 px-3 py-2 bg-emerald-50/50 rounded-lg border border-emerald-100">
                                        <Award className="w-4 h-4 text-emerald-500 flex-shrink-0" />
                                        <div className="flex-1 min-w-0">
                                            <p className="text-sm font-medium text-stone-800">{inv.applicant_name}</p>
                                            {inv.summary && <p className="text-xs text-stone-500 mt-0.5 truncate">{inv.summary}</p>}
                                        </div>
                                        <div className="flex items-center gap-2">
                                            <span className={`text-sm font-bold ${
                                                (inv.score || 0) >= 80 ? 'text-emerald-600' :
                                                (inv.score || 0) >= 60 ? 'text-amber-600' : 'text-rose-600'
                                            }`}>
                                                {inv.score != null ? `${Math.round(inv.score)}%` : '—'}
                                            </span>
                                            {inv.playback_url && (
                                                <a href={inv.playback_url} target="_blank" rel="noopener noreferrer"
                                                    className="flex items-center gap-1 px-2 py-1 rounded-lg bg-white border border-stone-200 text-xs text-stone-600 hover:bg-stone-50">
                                                    <Play className="w-3 h-3" /> Playback
                                                </a>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}

const PIPELINE_STAGES = [
    { key: 'applied', label: 'Applied', color: '#94a3b8', order: 0 },
    { key: 'initial_interview', label: 'Initial Interview', color: '#60a5fa', order: 1 },
    { key: 'matched_by_system', label: 'Matched', color: '#a78bfa', order: 2 },
    { key: 'endorsed_by_sourcer', label: 'Endorsed', color: '#818cf8', order: 3 },
    { key: 'initial_shortlist', label: 'Initial Shortlist', color: '#f59e0b', order: 5 },
    { key: 'client_shortlist', label: 'Client Shortlist', color: '#f97316', order: 6 },
    { key: 'client_interview', label: 'Client Interview', color: '#fb923c', order: 7 },
    { key: 'reference_check', label: 'Ref Check', color: '#2dd4bf', order: 8 },
    { key: 'job_offer', label: 'Job Offer', color: '#34d399', order: 9 },
    { key: 'hired', label: 'Hired', color: '#22c55e', order: 10 },
    { key: 'unsuccessful', label: 'Unsuccessful', color: '#ef4444', order: 11 },
];

function DroppableColumn({ stage, children }) {
    const { isOver, setNodeRef } = useDroppable({ id: stage.key });
    return (
        <div ref={setNodeRef}
            className={`bg-stone-50 rounded-lg border min-h-[200px] p-2 space-y-2 transition-colors ${
                isOver ? 'border-blue-400 bg-blue-50/50' : 'border-stone-200'
            }`}>
            {children}
        </div>
    );
}

function DraggableCard({ app, navigate }) {
    const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({
        id: app.application_id,
        data: { app },
    });
    const style = transform ? { transform: `translate(${transform.x}px, ${transform.y}px)` } : undefined;

    return (
        <div ref={setNodeRef} style={style}
            className={`bg-white rounded-lg border border-stone-200 p-3 shadow-sm hover:shadow transition-shadow ${isDragging ? 'opacity-50 shadow-lg' : ''}`}>
            <div className="flex items-start gap-1">
                <div {...listeners} {...attributes} className="cursor-grab active:cursor-grabbing mt-0.5 p-0.5 -ml-1 rounded hover:bg-stone-100">
                    <GripVertical className="w-3.5 h-3.5 text-stone-300" />
                </div>
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1">
                        <p className="font-medium text-sm text-stone-900 truncate">{app.applicant_name}</p>
                        <button onClick={() => navigate(`/recruitment/talent-pool/${app.applicant_id}`)}
                            className="p-0.5 rounded hover:bg-stone-100 flex-shrink-0" title="View profile">
                            <ExternalLink className="w-3 h-3 text-stone-400" />
                        </button>
                    </div>
                    <p className="text-xs text-stone-400 mt-0.5 truncate">{app.applicant_email}</p>
                    {app.ai_match_score != null && (
                        <div className="mt-2 flex items-center gap-1.5">
                            <div className="flex-1 h-1.5 bg-stone-100 rounded-full overflow-hidden">
                                <div className="h-full rounded-full bg-emerald-500"
                                    style={{ width: `${app.ai_match_score}%` }} />
                            </div>
                            <span className="text-xs font-medium text-stone-500">{app.ai_match_score}%</span>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function PipelineView({ stageGroups, applications, getAuthHeader, onRefresh, job }) {
    const [activeId, setActiveId] = useState(null);
    const [runningPipeline, setRunningPipeline] = useState(false);
    const [pipelineResult, setPipelineResult] = useState(null);
    const navigate = useNavigate();
    const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

    const handleRunMatchingPipeline = async () => {
        setRunningPipeline(true);
        setPipelineResult(null);
        try {
            const res = await axios.post(`${API}/ats/jobs/${job.job_id}/run-matching-pipeline`, {
                max_candidates: 50,
                skip_qa: false,
            }, { headers: getAuthHeader() });
            setPipelineResult(res.data);
            toast.success(`Matching complete: ${res.data?.matches || 0} matches found, ${res.data?.activated || 0} activated`);
            onRefresh?.();
        } catch (err) { toast.error(err.response?.data?.detail || 'Matching pipeline failed'); }
        finally { setRunningPipeline(false); }
    };

    const activeApp = activeId ? applications.find(a => a.application_id === activeId) : null;

    const handleDragStart = (event) => {
        setActiveId(event.active.id);
    };

    const handleDragEnd = async (event) => {
        setActiveId(null);
        const { active, over } = event;
        if (!over || !active) return;

        const app = applications.find(a => a.application_id === active.id);
        if (!app) return;

        const newStageKey = over.id;
        if (app.stage === newStageKey) return;

        const newStage = PIPELINE_STAGES.find(s => s.key === newStageKey);
        if (!newStage) return;

        try {
            await axios.patch(`${API}/ats/applications/${app.application_id}/stage`, {
                stage: newStage.key,
                stage_label: newStage.label,
                stage_order: newStage.order,
                move_reason: 'Moved via pipeline drag-and-drop',
            }, { headers: getAuthHeader() });
            toast.success(`${app.applicant_name} moved to ${newStage.label}`);
            onRefresh?.();
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Failed to move applicant');
        }
    };

    return (
        <div className="space-y-4">
            {/* Matching Pipeline Bar */}
            {job && (
                <div className="flex items-center justify-between bg-white rounded-xl border border-stone-200 px-4 py-3">
                    <div className="flex items-center gap-3">
                        <Target className="w-5 h-5 text-violet-500" />
                        <div>
                            <p className="text-sm font-medium text-stone-800">AI Matching Pipeline</p>
                            <p className="text-xs text-stone-400">Match → QA → Review → Activate</p>
                        </div>
                    </div>
                    <div className="flex items-center gap-3">
                        {pipelineResult && (
                            <div className="flex items-center gap-2 text-xs text-stone-500">
                                <span>{pipelineResult.evaluated} evaluated</span>
                                <span>→</span>
                                <span className="text-emerald-600 font-medium">{pipelineResult.matches} matched</span>
                                <span>→</span>
                                <span className="text-blue-600 font-medium">{pipelineResult.activated} activated</span>
                            </div>
                        )}
                        <Button onClick={handleRunMatchingPipeline} disabled={runningPipeline}
                            className="bg-violet-600 text-white hover:bg-violet-700" size="sm">
                            {runningPipeline
                                ? <><Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> Running Pipeline...</>
                                : <><Sparkles className="w-3.5 h-3.5 mr-1" /> Run Matching</>}
                        </Button>
                    </div>
                </div>
            )}

            {applications.length === 0 ? (
                <div className="text-center py-12 bg-white rounded-xl border border-stone-200">
                    <Users className="w-10 h-10 text-stone-300 mx-auto mb-3" />
                    <p className="text-sm text-stone-500">No applicants in the pipeline yet</p>
                    <p className="text-xs text-stone-400 mt-1">Applicants will appear here when they apply or are added, or run AI Matching above</p>
                </div>
            ) : (

        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragStart={handleDragStart} onDragEnd={handleDragEnd}>
            <div className="overflow-x-auto pb-4">
                <div className="flex gap-3 min-w-max">
                    {PIPELINE_STAGES.map(stage => {
                        const apps = stageGroups[stage.key] || [];
                        return (
                            <div key={stage.key} className="w-56 flex-shrink-0">
                                <div className="flex items-center gap-2 mb-2 px-1">
                                    <div className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: stage.color }} />
                                    <span className="text-xs font-semibold text-stone-700">{stage.label}</span>
                                    <span className="text-xs text-stone-400 ml-auto">{apps.length}</span>
                                </div>
                                <DroppableColumn stage={stage}>
                                    {apps.map(app => (
                                        <DraggableCard key={app.application_id} app={app} navigate={navigate} />
                                    ))}
                                </DroppableColumn>
                            </div>
                        );
                    })}
                </div>
            </div>
            <DragOverlay>
                {activeApp ? (
                    <div className="bg-white rounded-lg border-2 border-blue-400 p-3 shadow-xl w-56 rotate-2">
                        <p className="font-medium text-sm text-stone-900">{activeApp.applicant_name}</p>
                        <p className="text-xs text-stone-400 mt-0.5">{activeApp.applicant_email}</p>
                    </div>
                ) : null}
            </DragOverlay>
        </DndContext>
            )}
        </div>
    );
}
